/* eslint-disable formatjs/no-literal-string-in-jsx -- Please check and fix */
import type { RadioGroupProps as AriaRadioGroupProps } from 'react-aria-components';
import { Label, RadioGroup as AriaRadioGroup, FieldError, Button } from 'react-aria-components';
import { forwardRef, type ReactElement, type ReactNode } from 'react';
import cx from 'clsx';
import { IconErrorOutlined } from '@repo/monochrome-icons';
import type { ForwardRefType } from '#types/forward-ref';
import { InfoIcon } from '../../assets/icons';
import { Tooltip } from '../tooltip';
import { Description } from '../form-elements';
import styles from './radio-group.module.css';

export interface RadioGroupProps<T extends string> extends Omit<AriaRadioGroupProps, 'onChange'> {
  label?: ReactNode;
  description?: string | ReactNode | null;
  errorMessage?: ReactNode;
  defaultValue?: T;
  value?: T;
  onChange?: (value: T) => void;
  tooltipText?: string;
  labelClassName?: string;
}

export const RadioGroup = (forwardRef as ForwardRefType)(function RadioGroup<T extends string>(
  {
    label,
    description,
    errorMessage,
    children,
    className,
    tooltipText,
    onChange: onChangeProps,
    labelClassName = 'body-standard',
    ...props
  }: RadioGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  const mappedProps = onChangeProps
    ? {
        ...props,
        onChange: (value: string): void => {
          onChangeProps(value as T);
        },
      }
    : props;

  return (
    <AriaRadioGroup
      className={cx(styles.wrapper, className)}
      ref={ref}
      {...mappedProps}
      data-test-radio-group
    >
      {state => (
        <>
          {label ? (
            <Label className={cx(styles['group-label'], labelClassName)}>
              {label}
              {tooltipText ? (
                <Tooltip label={tooltipText} placement="top">
                  <Button aria-label="tooltip" className={cx(styles.tooltip, 'ml-8')}>
                    <InfoIcon />
                  </Button>
                </Tooltip>
              ) : null}
            </Label>
          ) : null}
          {typeof children === 'function' ? children(state) : children}
          {Boolean(description) && <Description>{description}</Description>}
          {errorMessage ? (
            <FieldError className={styles.error}>
              <IconErrorOutlined className="mr-8" />
              {errorMessage}
            </FieldError>
          ) : null}
        </>
      )}
    </AriaRadioGroup>
  );
});
